import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import routes from "routes/routes";
import {
  useCancelOrganizationBookingMutation,
  useGetOrganizationBookingsQuery,
  useGetOrganizationHallTimesQuery,
} from "api/account/accountApiSlice";
import { OrganizationPlanBooking } from "api/account/types";
import AccountPageLayout from "../components/AccountPageLayout";
import { atStation, zoneOfLocation } from "util/stationTime";
import { ORGANIZATION_ID_PARAM } from "../common/organizationAdmin";
import { makeOrganizationSeriesPath } from "../common/organizationSeries";
import { FIRST_WEEKS, makeOrganizationHallTimesPath } from "../common/organizationHallTimes";
import {
  ALL,
  bookerOptions,
  filterPlan,
  resourceLabel,
  resourceOptions,
} from "../common/organizationPlan";

/** The owner cancel's refusal codes, each with its own `organization_plan.*` text. */
const CANCEL_FAILURE_TEXTS: Record<string, string> = {
  already_canceled: "cancel_already",
  already_started: "cancel_started",
  not_self_cancelable: "cancel_paid",
  // Paid or invoiced between the list and the click (409); the mutation's
  // tags reload the list either way.
  booking_changed: "cancel_changed",
};

/** Which text says why an owner cancel failed — 404 = no longer allowed. */
const cancelFailureText = (status: number | undefined, code: string | undefined): string => {
  if (status === 404) return "cancel_not_allowed";
  return (code && CANCEL_FAILURE_TEXTS[code]) || "cancel_error";
};

/**
 * The organization's booking plan (#3037, M6 of the serial-use plan).
 *
 * Every approved member sees it; only owners cancel (PO, 2026-10-03). What the
 * page shows is decided by the server — `canManage` and each row's
 * `cancelable` come from the response and are re-derived on every request, so
 * nothing here is a rule, only its explanation (ADR-0020).
 *
 * Resource-neutral: a row names whatever was booked (room, object, person) and
 * nothing here assumes it is a room.
 */
const OrganizationBookingsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const organizationId = String(params[ORGANIZATION_ID_PARAM] ?? "");

  const { data, isLoading, isError, error } = useGetOrganizationBookingsQuery(
    { organizationId },
    { skip: organizationId.length === 0 }
  );
  const [cancelBooking, { isLoading: isCanceling }] = useCancelOrganizationBookingMutation();
  // "Our hall times" is offered only to an organisation that has any (W2-1);
  // the same first span the page loads, so it opens from the cache.
  const { data: hallTimes } = useGetOrganizationHallTimesQuery(
    { organizationId, weeks: FIRST_WEEKS },
    { skip: organizationId.length === 0 }
  );

  const [resourceFilter, setResourceFilter] = React.useState<string>(ALL);
  const [bookerFilter, setBookerFilter] = React.useState<string>(ALL);
  const [pendingCancel, setPendingCancel] = React.useState<OrganizationPlanBooking | null>(null);
  const [actionMessage, setActionMessage] = React.useState<string | null>(null);

  // The organization is context: switching to another must not carry its
  // filters or a half-confirmed cancellation across.
  React.useEffect(() => {
    setResourceFilter(ALL);
    setBookerFilter(ALL);
    setPendingCancel(null);
    setActionMessage(null);
  }, [organizationId]);

  // Shape-check, never `?? []`: an unexpected body is truthy and `.map` on it
  // would take the page down.
  const bookings: OrganizationPlanBooking[] = React.useMemo(
    () => (Array.isArray(data?.bookings) ? data!.bookings : []),
    [data]
  );
  const resources = React.useMemo(() => resourceOptions(bookings, i18n.language), [bookings, i18n.language]);
  const bookers = React.useMemo(() => bookerOptions(bookings), [bookings]);
  const visible = React.useMemo(
    () => filterPlan(bookings, resourceFilter, bookerFilter),
    [bookings, resourceFilter, bookerFilter]
  );

  // Intl rather than moment: moment's de/nb locales are only loaded as a side
  // effect of the booking date picker, so this page would format in English
  // whenever it is opened first. Days and times are the station's clock
  // (ADR-0019 rule 2) — each booking's own location zone (#3219) — not the
  // phone's, and so is the same-day check.
  const formatWhen = (booking: OrganizationPlanBooking): string => {
    if (!booking.startDate) return t("common.not_applicable");
    const lang = i18n.language;
    const timeZone = zoneOfLocation({ timeZone: booking.locationTimeZone });
    const start = new Date(booking.startDate);
    const end = booking.endDate ? new Date(booking.endDate) : null;
    const day = (d: Date) =>
      d.toLocaleDateString(lang, {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone,
      });
    const time = (d: Date) =>
      d.toLocaleTimeString(lang, { hour: "2-digit", minute: "2-digit", timeZone });
    if (!end) return `${day(start)} · ${time(start)}`;
    return atStation(end, timeZone).isSame(atStation(start, timeZone), "day")
      ? `${day(start)} · ${time(start)}–${time(end)}`
      : `${day(start)} ${time(start)} – ${day(end)} ${time(end)}`;
  };

  const confirmCancel = async () => {
    if (!pendingCancel) return;
    setActionMessage(null);
    try {
      await cancelBooking({ organizationId, orderId: pendingCancel.id }).unwrap();
      setActionMessage(
        t("account.organization_plan.canceled", { number: pendingCancel.bookingNumber })
      );
    } catch (err) {
      // Say WHY — the server sends a reason code for every refusal, and a
      // generic failure would hide which one it was.
      const e = err as { status?: number; data?: { code?: string } };
      setActionMessage(t(`account.organization_plan.${cancelFailureText(e?.status, e?.data?.code)}`));
    } finally {
      setPendingCancel(null);
    }
  };

  // Nothing in the plan at all vs. nothing matching the filters — two facts.
  let emptyMessage: string | null = null;
  if (bookings.length === 0) emptyMessage = t("account.organization_plan.empty");
  else if (visible.length === 0) emptyMessage = t("account.organization_plan.no_match");

  if (isLoading) {
    return (
      <AccountPageLayout title={t("account.organization_plan.title")}>
        <p className="text-sm text-slate-600">{t("common.loading")}</p>
      </AccountPageLayout>
    );
  }

  // A 404 is not "no bookings": the person is no longer a member, or the
  // organization was archived. Say so with a way back — never an empty list.
  if (isError || !data) {
    const status = (error as { status?: number } | undefined)?.status;
    return (
      <AccountPageLayout title={t("account.organization_plan.title")}>
        <p className="text-sm text-slate-600">
          {status === 404
            ? t("account.organization_plan.no_longer_member")
            : t("account.organization_plan.load_error")}
        </p>
        <Button variant="outline" onClick={() => navigate(routes.accountPath)}>
          {t("account.organization_admin.back_to_account")}
        </Button>
      </AccountPageLayout>
    );
  }

  return (
    <AccountPageLayout title={data.organizationName}>
      <p className="text-sm text-slate-600">
        {data.canManage
          ? t("account.organization_plan.intro_owner")
          : t("account.organization_plan.intro_member")}
      </p>

      {data.canManage && (
        <Button className="w-full" onClick={() => navigate(makeOrganizationSeriesPath(organizationId))}>
          {t("account.organization_series.open")}
        </Button>
      )}

      {hallTimes?.hasWindows && (
        <Button
          variant="outline"
          className="w-full"
          onClick={() => navigate(makeOrganizationHallTimesPath(organizationId))}
        >
          {t("account.organization_hall_times.open")}
        </Button>
      )}

      {actionMessage && (
        <div className="rounded-md border border-foreground/15 px-3 py-2 text-sm text-foreground">
          {actionMessage}
        </div>
      )}

      {bookings.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700" htmlFor="org-plan-resource">
              {t("account.organization_plan.filter_resource")}
            </label>
            <Select value={resourceFilter} onValueChange={setResourceFilter}>
              <SelectTrigger id="org-plan-resource">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("account.organization_plan.filter_all")}</SelectItem>
                {resources.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700" htmlFor="org-plan-booker">
              {t("account.organization_plan.filter_member")}
            </label>
            <Select value={bookerFilter} onValueChange={setBookerFilter}>
              <SelectTrigger id="org-plan-booker">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("account.organization_plan.filter_all")}</SelectItem>
                {bookers.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label ?? t("common.not_applicable")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {emptyMessage ? (
        <p className="text-sm text-slate-600">{emptyMessage}</p>
      ) : (
        <ul className="space-y-2">
          {visible.map((booking) => (
            <li
              key={booking.id}
              className="flex items-start justify-between gap-3 rounded-md bg-primary/5 px-3 py-2"
            >
              <div className="min-w-0">
                <p className="text-sm text-foreground">{formatWhen(booking)}</p>
                <p className="truncate text-sm text-foreground">
                  {resourceLabel(booking, i18n.language) ?? t("common.not_applicable")}
                </p>
                <p className="truncate text-xs text-slate-600">
                  {booking.bookerName ?? t("common.not_applicable")}
                  {" · "}
                  {t(`account.organization_plan.status.${booking.status}`)}
                </p>
              </div>
              {booking.cancelable && (
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() => setPendingCancel(booking)}
                >
                  {t("account.organization_plan.cancel")}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {data.truncated && (
        <p className="text-xs text-slate-600">{t("account.organization_plan.truncated")}</p>
      )}

      {pendingCancel && (
        <div className="space-y-3 rounded-md border border-foreground/15 px-3 py-3">
          <p className="text-sm text-foreground">
            {t("account.organization_plan.cancel_confirm", {
              when: formatWhen(pendingCancel),
              name: pendingCancel.bookerName ?? t("common.not_applicable"),
            })}
          </p>
          <p className="text-xs text-slate-600">
            {t("account.organization_plan.cancel_consequence")}
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setPendingCancel(null)}>
              {t("account.organization_plan.keep")}
            </Button>
            <Button onClick={confirmCancel} disabled={isCanceling}>
              {isCanceling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("account.organization_plan.cancel")}
            </Button>
          </div>
        </div>
      )}

      <Button variant="outline" className="w-full" onClick={() => navigate(routes.accountPath)}>
        {t("account.organization_admin.back_to_account")}
      </Button>
    </AccountPageLayout>
  );
};

export default OrganizationBookingsPage;
